import { useConstCallback, useDebouncedCallback } from '@workday/wcp-cargo-react';
import { useRef } from 'react';

interface ContainerSize {
  width?: number;
  height?: number;
}
interface MeasuredModel {
  events: { setContainerSize: (size: ContainerSize) => void };
}

/** Measures overflow immediately on mount, then waits for resizing to settle before updating the collection. */
export const useSettledContainerModel = <T extends MeasuredModel>(model: T): T => {
  const lastMeasurementRef = useRef<ContainerSize | null>(null);
  const updateSize = useConstCallback((size: ContainerSize) => model.events.setContainerSize(size));
  const updateWhenIdle = useDebouncedCallback(updateSize, { delay: 100 });
  const measure = useConstCallback((size: ContainerSize) => {
    const previous = lastMeasurementRef.current;
    if (previous?.width === size.width && previous?.height === size.height) return;
    lastMeasurementRef.current = size;
    if (!previous) updateSize(size);
    else void updateWhenIdle(size);
  });
  return { ...model, events: { ...model.events, setContainerSize: measure } };
};
